import { useMsal } from '@azure/msal-react';
import {
    Stack,
    Text,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    ICommandBarItemProps
} from '@fluentui/react';
import { RootState } from 'app/store';
import { useEffect, useState, useMemo, useRef } from 'react';
import { ConnectedProps, connect, useDispatch, useSelector } from 'react-redux';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import { AppDataSource } from 'global/DataSources/AppDataSource';
import { setAssociationTemplateSnapshot, addUpdateBenefit } from 'global/Reducer/dataSlice';
import { stampAssociationTemplateProvenance, loadAssociationTemplateSnapshotAction, ESaveType } from 'global/Application/dataActions';
import { useApplication } from 'global/Application/Context';
import {
    IBenefit as FAM_IBenefit,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IPlan as FAM_IPlan
} from 'model/FlexibleAttributeModel';
import { v4 as uuidv4 } from 'uuid';
import CustomPanel from 'components/controls/CustomPanel';
import { benefitNamesMatch, categoriesMatch } from './associationTemplateUtils';
import GroupConfigurationSnapshotContext from './GroupConfigurationSnapshotContext';
import { IComponentContextMenu } from 'model/Application';
import BenefitComparisonPanel from './BenefitComparisonPanel';
import { useHistory } from 'react-router-dom';
import SnapshotLookup from './Controls/SnapshotLookup';

interface IProps extends TComponentConnectedProps, IComponentContextMenu {
}

// Splits a plan name into a set of lowercase word tokens, stripping punctuation.
// e.g. "HV High Option -FI - Four Tier - H4" -> {"hv","high","option","fi","four","tier","h4"}
const tokenizePlanName = (name: string): Set<string> =>
    new Set(name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean));

// Two plan names match when:
//   1. Their numeric tokens are identical sets — "11" must pair with "11", not "2".
//   2. The non-numeric word tokens satisfy subset containment for broader/narrower name cases.
// "High Option" <-> "HV High Option -FI - Four Tier - H4" -> no numeric tokens, subset {"high","option"} ⊆ larger -> match
// "Plan 11- Medical" <-> "Medical - Plan 2"  -> numeric {11}≠{2} -> no match
const planNamesMatch = (a: string, b: string): boolean => {
    const tokA = tokenizePlanName(a);
    const tokB = tokenizePlanName(b);
    const numA = new Set([...tokA].filter(t => /^\d+$/.test(t)));
    const numB = new Set([...tokB].filter(t => /^\d+$/.test(t)));
    if (numA.size !== numB.size) return false;
    if (![...numA].every(n => numB.has(n))) return false;
    const [smaller, larger] = tokA.size <= tokB.size ? [tokA, tokB] : [tokB, tokA];
    return [...smaller].every(t => larger.has(t));
};

// Default Association Template - BR account and snapshot
const DEFAULT_TEMPLATE_ACCOUNT: FAM_IEntityReferenceValue = {
    id: '09fb458d-49d8-eb11-bacb-000d3a533755',
    name: 'Association Template',
    type: 'entity',
    entityType: 'account'
};

const DEFAULT_TEMPLATE_SNAPSHOT: FAM_IEntityReferenceValue = {
    id: '4ba75527-05c0-f011-bbd3-6045bda8e172',
    name: 'Major Group Associations [Master]',
    type: 'entity',
    entityType: 'bcbs_groupconfigurationsnapshot'
};

const AssociationTemplates = (props: IProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const appContext = useApplication();
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    // Get the loaded template from Redux
    const associationTemplateSnapshot = useSelector((state: RootState) => state.data.associationTemplateSnapshot);

    // Get current configuration data from props (connected via Redux)
    // Use useMemo to prevent recalculation on every render
    const currentBenefits = useMemo(() => 
        (props.state.data.json?.benefits || []).filter((b: FAM_IBenefit) => !b.deactivated),
        [props.state.data.json?.benefits]
    );
    const currentConfig = props.state.data.config;
    const currentSnapshot = props.state.data.snapshot;
    const currentJson = props.state.data.json;

    const [selectedAccount, setSelectedAccount] = useState<FAM_IEntityReferenceValue | null>(DEFAULT_TEMPLATE_ACCOUNT);
    const [selectedSnapshot, setSelectedSnapshot] = useState<FAM_IEntityReferenceValue | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Use a ref to store template benefits - refs don't cause re-renders when changed
    const templateBenefitsRef = useRef<FAM_IBenefit[]>([]);
    const [templateBenefitsVersion, setTemplateBenefitsVersion] = useState(0);

    // Track benefits that were added from the template (by benefit ID)
    const [templateAddedBenefitIds, setTemplateAddedBenefitIds] = useState<Set<string>>(new Set());

    // Track staged benefits (not yet applied to Redux)
    const [stagedBenefits, setStagedBenefits] = useState<FAM_IBenefit[]>([]);

    // Merge current benefits with staged benefits for display
    const displayCurrentBenefits = useMemo(() => {
        // Start with actual current benefits from Redux
        const merged = [...currentBenefits];

        // Add or update with staged benefits
        stagedBenefits.forEach(stagedBenefit => {
            const existingIndex = merged.findIndex(b => b.id === stagedBenefit.id);
            if (existingIndex >= 0) {
                // Replace existing benefit with staged version (override case)
                merged[existingIndex] = stagedBenefit;
            } else {
                // Add new staged benefit
                merged.push(stagedBenefit);
            }
        });

        return merged;
    }, [currentBenefits, stagedBenefits]);

    // Count committed template benefits
    const committedTemplateBenefitsCount = useMemo(() => {
        return currentBenefits.filter((b: FAM_IBenefit) =>
            b.bcbs_fromassociationtemplate === true && 
            !b.deactivated
        ).length;
    }, [currentBenefits]);

    useEffect(() => {
        document.title = `Association Templates | ${props.state.data.snapshot?.bcbs_name ?? '-'}`;
    }, []);

    // Sync templateBenefitsRef and show success message when associationTemplateSnapshot changes
    useEffect(() => {
        if (associationTemplateSnapshot?.json?.benefits && templateBenefitsRef.current.length === 0) {
            templateBenefitsRef.current = associationTemplateSnapshot.json.benefits;
            setTemplateBenefitsVersion(v => v + 1); // Trigger re-render
            const benefitCount = associationTemplateSnapshot.json.benefits.length;
            setSuccess(`Template loaded successfully! Found ${benefitCount} benefit${benefitCount !== 1 ? 's' : ''}.`);

            // Seed templateAddedBenefitIds with any current benefits that were previously
            // added from this template (match via bcbs_associationtemplatebenefitid).
            // This must run here (not at mount) because the template IDs are only known
            // after the snapshot loads.
            const templateBenefitIdSet = new Set(
                associationTemplateSnapshot.json.benefits.map((b: FAM_IBenefit) => b.id)
            );
            const alreadyAddedTemplateIds = currentBenefits
                .filter((b: FAM_IBenefit) =>
                    b.bcbs_fromassociationtemplate === true &&
                    !!b.bcbs_associationtemplatebenefitid &&
                    templateBenefitIdSet.has(b.bcbs_associationtemplatebenefitid)
                )
                .map((b: FAM_IBenefit) => b.bcbs_associationtemplatebenefitid as string);
            if (alreadyAddedTemplateIds.length > 0) {
                setTemplateAddedBenefitIds(new Set(alreadyAddedTemplateIds));
            }
        }
    }, [associationTemplateSnapshot]);

    const handleLoadTemplate = async () => {
        if (!selectedSnapshot) {
            setError('Please select an Association Template snapshot.');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            await dispatch(loadAssociationTemplateSnapshotAction(dataSource, selectedSnapshot.id) as any);

        } catch (err: any) {
            console.error('Error loading Association Template:', err);
            setError(err?.message || 'An error occurred while loading the template.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddBenefitFromTemplate = (templateBenefit: FAM_IBenefit) => {
        try {
            // Check in displayCurrentBenefits which includes both current and staged
            const existingBenefit = displayCurrentBenefits.find(
                (b: FAM_IBenefit) => {
                    // First check by ID (in case it's literally the same object)
                    if (b.id === templateBenefit.id) return true;
                    // Match by category + fuzzy name (e.g. "Plan 1" matches "Medical - Plan 1")
                    return categoriesMatch(b.bcbs_config_categoryid, templateBenefit.bcbs_config_categoryid)
                        && benefitNamesMatch(b.name ?? '', templateBenefit.name ?? '');
                }
            );

            // If a matching benefit already exists, replace it (reuse its ID so the
            // reducer overwrites it rather than appending a duplicate).
            addBenefitToConfiguration(templateBenefit, existingBenefit ?? undefined);
        } catch (err: any) {
            console.error('Error in handleAddBenefitFromTemplate:', err);
            setError(err?.message || 'An error occurred while processing the benefit.');
        }
    };

    const addBenefitToConfiguration = (templateBenefit: FAM_IBenefit, existingBenefit?: FAM_IBenefit) => {
        try {
            if (!currentJson) {
                throw new Error('Configuration JSON data is not loaded.');
            }
            if (!templateBenefit) {
                throw new Error('Template benefit data is missing.');
            }

            const clonedBenefit: FAM_IBenefit = JSON.parse(JSON.stringify(templateBenefit));
            stampAssociationTemplateProvenance(
                clonedBenefit,
                templateBenefit.id,
                selectedSnapshot?.id ?? associationTemplateSnapshot?.snapshot?.bcbs_groupconfigurationsnapshotid ?? undefined
            );
            // Reuse the existing benefit's ID and name so the reducer replaces it
            // and the display name stays consistent with the existing configuration.
            // Generate a new ID only for genuinely new benefits.
            clonedBenefit.id = existingBenefit ? existingBenefit.id : uuidv4();
            if (existingBenefit) clonedBenefit.name = existingBenefit.name;
            const successMsg = existingBenefit
                ? `"${existingBenefit.name}" replaced from template's "${templateBenefit.name}". Click "Apply Template Changes" to commit.`
                : `Benefit "${templateBenefit.name}" added from template. Click "Apply Template Changes" to commit.`;
            setSuccess(successMsg);

            // Add to staged benefits instead of dispatching immediately
            setStagedBenefits(prev => {
                // Remove any existing staged benefit with the same ID (replace/override)
                const filtered = prev.filter(b => b.id !== clonedBenefit.id);
                return [...filtered, clonedBenefit];
            });

            setTemplateAddedBenefitIds(prev => new Set(prev).add(templateBenefit.id));

        } catch (err: any) {
            console.error('Error adding benefit:', err);
            setError(err?.message || 'An error occurred while adding the benefit.');
        }
    };



    const handleResyncFromTemplate = () => {
        try {
            const templateBenefits = templateBenefitsRef.current;

            if (!templateBenefits || templateBenefits.length === 0) {
                setError('No template loaded. Please select a snapshot first.');
                return;
            }

            const templateSourcedBenefits = currentBenefits.filter(
                (b: FAM_IBenefit) => b.bcbs_fromassociationtemplate === true
            );

            if (templateSourcedBenefits.length === 0) {
                setError('No template benefits found in the current configuration to re-sync.');
                return;
            }

            const resynced: FAM_IBenefit[] = [];
            const notFound: string[] = [];

            templateSourcedBenefits.forEach((currentBenefit: FAM_IBenefit) => {
                const templateBenefit =
                    (currentBenefit.bcbs_associationtemplatebenefitid
                        ? templateBenefits.find((t: FAM_IBenefit) => t.id === currentBenefit.bcbs_associationtemplatebenefitid && !t.deactivated)
                        : undefined)
                    ?? templateBenefits.find((t: FAM_IBenefit) =>
                        t.bcbs_config_categoryid === currentBenefit.bcbs_config_categoryid &&
                        t.name === currentBenefit.name &&
                        !t.deactivated
                    );

                if (!templateBenefit) {
                    notFound.push(currentBenefit.name);
                    return;
                }

                const resyncedBenefit: FAM_IBenefit = JSON.parse(JSON.stringify(currentBenefit));
                resyncedBenefit.name = templateBenefit.name;
                resyncedBenefit.bcbs_name = templateBenefit.bcbs_name;
                resyncedBenefit.fields = JSON.parse(JSON.stringify(templateBenefit.fields));
                resyncedBenefit.tiers = JSON.parse(JSON.stringify(templateBenefit.tiers));
                resyncedBenefit.bcbs_associationtemplatebenefitid = templateBenefit.id;
                resyncedBenefit.bcbs_associationtemplatesnapshotid = selectedSnapshot?.id ?? associationTemplateSnapshot?.snapshot?.bcbs_groupconfigurationsnapshotid ?? null;
                // bcbs_associationtemplateaction is intentionally not overwritten here —
                // re-sync preserves the original action (Import or Sync) that was set when the benefit was first linked.
                resynced.push(resyncedBenefit);
            });

            if (resynced.length > 0) {
                setStagedBenefits(prev => {
                    const updated = [...prev];
                    resynced.forEach(b => {
                        const idx = updated.findIndex(s => s.id === b.id);
                        if (idx > -1) updated[idx] = b;
                        else updated.push(b);
                    });
                    return updated;
                });
            }

            let message = `Re-synced ${resynced.length} benefit(s) from template.`;
            if (notFound.length > 0)
                message += ` ${notFound.length} could not be matched: ${notFound.join(', ')}.`;
            message += ' Click "Apply Template Changes" to commit.';
            setSuccess(message);

        } catch (err: any) {
            console.error('Error re-syncing from template:', err);
            setError(err?.message || 'An error occurred while re-syncing from template.');
        }
    };

    const handleApplyTemplateChanges = () => {
        try {
            if (stagedBenefits.length === 0) {
                setError('No changes to apply. Add benefits first.');
                return;
            }

            // Dispatch all staged benefits at once
            dispatch(addUpdateBenefit(stagedBenefits));

            const message = `Applied ${stagedBenefits.length} benefit(s) to configuration.`;

            setSuccess(message);

            // Clear staged changes
            setStagedBenefits([]);

            // Trigger the actual save to the backend
            appContext.SaveAsync({ saveType: ESaveType.EManual });

            // Navigate back to Group Configuration with success state
            const groupId = props.state.data.snapshot?._bcbs_groupid_value;
            const snapshotId = props.state.data.snapshot?.bcbs_groupconfigurationsnapshotid;
            if (groupId && snapshotId) {
                history.push({
                    pathname: `/records/blueRelationship/${groupId}/groupConfiguration/${snapshotId}`,
                    state: { 
                        associationTemplateMessage: message,
                        associationTemplateMessageType: 'success'
                    }
                });
            }
        } catch (err: any) {
            console.error('Error applying template changes:', err);
            setError(err?.message || 'An error occurred while applying template changes.');
        }
    };

    const contextMenu = useMemo(() => {
        const menu: ICommandBarItemProps[] = [];

        // Add "Apply Template Changes" button
        menu.push({
            key: 'applyTemplateChanges',
            text: 'Apply Template Changes',
            iconProps: { iconName: 'Accept' },
            disabled: stagedBenefits.length === 0,
            onClick: handleApplyTemplateChanges
        });

        // Add "Re-Sync from Template" button
        menu.push({
            key: 'resyncFromTemplate',
            text: 'Re-Sync from Template',
            iconProps: { iconName: 'Sync' },
            disabled: templateBenefitsRef.current.length === 0 || committedTemplateBenefitsCount === 0,
            onClick: handleResyncFromTemplate
        });

        return { items: menu, key: 'associationTemplates' };
    }, [stagedBenefits.length, committedTemplateBenefitsCount, templateBenefitsVersion]);

    // Use local state if available, otherwise fall back to Redux store
    // Once loaded, always use the ref to prevent disappearing during Redux updates
    const displayTemplateBenefits = useMemo(() => {
        return templateBenefitsRef.current.length > 0
            ? templateBenefitsRef.current
            : associationTemplateSnapshot?.json?.benefits || [];
    }, [templateBenefitsVersion, associationTemplateSnapshot?.json?.benefits]);

    // Build the list of template plans that overlap with the current config plans (for the plan filter dropdown)
    const displayTemplatePlans = useMemo((): FAM_IPlan[] => {
        const currentPlanNamesArr = (currentJson?.plans ?? [])
            .filter((p: FAM_IPlan) => !p.deactivated)
            .map((p: FAM_IPlan) => p.name);
        if (currentPlanNamesArr.length === 0) return [];
        return (associationTemplateSnapshot?.json?.plans ?? [])
            .filter((p: FAM_IPlan) =>
                !p.deactivated &&
                currentPlanNamesArr.some(curName => planNamesMatch(curName, p.name))
            );
    }, [associationTemplateSnapshot?.json?.plans, currentJson?.plans]);

    const templateName = associationTemplateSnapshot?.account?.name || 'Association Template';

    return (
        <GroupConfigurationSnapshotContext 
            contextMenu={contextMenu}
            hideShowDeactivated={true}
            hideSave={true}
        >
            <CustomPanel
                title={`Association Template: ${templateName}`}
                canCollapse={false}
                defaultCollapsed={false}
                storageId="associationTemplates.main"
                onRenderHeaderContent={() => (
                    <div style={{ marginLeft: '15px', fontWeight: 450 }}>
                        Association Template: {templateName}
                    </div>
                )}
            >
                <Stack tokens={{ childrenGap: 15 }} style={{ padding: '15px' }}>
                    {/* Snapshot Selection */}
                    <SnapshotLookup
                        label="Association Template Snapshot"
                        onPick={async (value) => {
                            if (value) {
                                setSelectedSnapshot(value);
                                setLoading(true);
                                setError(null);
                                setSuccess(null);

                                try {
                                    // Get the full snapshot record first
                                    const snapshot = await dataSource.getGroupConfigurationSnapshot(value.id);

                                    console.log('Loaded snapshot record:', snapshot);

                                    if (!snapshot._bcbs_groupid_value) {
                                        setError('Association Template snapshot is missing account reference.');
                                        return;
                                    }

                                    // Use downloadGroupConfigurationSnapshot to get the JSON (handles blob storage)
                                    const result = await dataSource.downloadGroupConfigurationSnapshot(snapshot);

                                    if (!result || !result.json) {
                                        setError('Failed to download snapshot JSON data.');
                                        return;
                                    }

                                    const snapshotJson = result.json;

                                    console.log('Downloaded JSON:', snapshotJson);
                                    console.log('Benefits count:', snapshotJson?.benefits?.length);

                                    if (!snapshotJson?.benefits || snapshotJson.benefits.length === 0) {
                                        setError(`Association template snapshot "${snapshot.bcbs_name || value.name}" has no benefits.`);
                                        return;
                                    }

                                    // Load the account record
                                    const account = await dataSource.getAccount(snapshot._bcbs_groupid_value);

                                    dispatch(setAssociationTemplateSnapshot({
                                        snapshot: snapshot,
                                        json: snapshotJson,
                                        config: {} as FAM_IGroupConfigurationConfig,
                                        account: account
                                    }));

                                    // Store template benefits in ref
                                    templateBenefitsRef.current = snapshotJson.benefits || [];
                                    setTemplateBenefitsVersion(v => v + 1); // Trigger re-render

                                    setSuccess(`Template loaded successfully: ${snapshot.bcbs_name || value.name} (${snapshotJson.benefits.length} benefits)`);
                                    setError(null);
                                } catch (err: any) {
                                    console.error('Error loading template:', err);
                                    setError(`Failed to load template: ${err.message || 'Unknown error'}`);
                                } finally {
                                    setLoading(false);
                                }
                            }
                        }}
                        valueAccount={DEFAULT_TEMPLATE_ACCOUNT}
                        defaultValueAccount={DEFAULT_TEMPLATE_ACCOUNT}
                        defaultValueViewKey="associationTemplateView"
                        searchOnMount={true}
                        disabled={loading}
                    />

                    {/* Messages */}
                    {loading && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            <Spinner size={SpinnerSize.small} style={{ marginRight: 8 }} />
                            Loading template...
                        </MessageBar>
                    )}

                    {error && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            onDismiss={() => setError(null)}
                            dismissButtonAriaLabel="Close"
                        >
                            {error}
                        </MessageBar>
                    )}

                    {success && (
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            onDismiss={() => setSuccess(null)}
                            dismissButtonAriaLabel="Close"
                        >
                            {success}
                        </MessageBar>
                    )}

                    {/* Benefit Comparison Section */}
                    <BenefitComparisonPanel
                        templateBenefits={displayTemplateBenefits}
                        templatePlans={displayTemplatePlans}
                        currentBenefits={displayCurrentBenefits}
                        templateAddedBenefitIds={templateAddedBenefitIds}
                        stagedBenefitsCount={stagedBenefits.length}
                        committedTemplateBenefitsCount={committedTemplateBenefitsCount}
                        loading={loading}
                        onAddBenefit={handleAddBenefitFromTemplate}
                    />
                </Stack>
            </CustomPanel>
        </GroupConfigurationSnapshotContext>
    );
};

function mapStateToProps(state: RootState) { 
    return { 
        state: {
            data: {
                snapshot: state.data.snapshot,
                config: state.data.config,
                json: state.data.json,
                account: state.data.account,
                associationTemplateSnapshot: state.data.associationTemplateSnapshot
            },
            status: state.status
        }
    } 
}
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(AssociationTemplates);
